import { translate } from '../../../utils/translator';
import { Product } from './ReturningCustomersLayout';

// Dosage form suffix on a treatment card title ("Mounjaro" -> "Mounjaro Injection").
//
// Until now the form was only visible when someone had typed it into the catalog title
// ("Wegovy Pill", "Foundayo® Pill"), so the pill catalogs announced their form while the pen ones
// (Mounjaro, Wegovy) said nothing and read as if they were pills too. The form is already in the
// DB as products.quantity_type and reaches the client on every product, so it is appended from
// there instead of relying on the catalog name.

// additional_data.overrided_product_type wins over quantity_type when set — the same precedence
// the preferred-type sorting in ProductSelection already applies.
export const resolveDosageForm = (product?: Product | null): string => {
  if (!product) return '';
  const raw = product.additional_data;
  let additionalData: Record<string, any> = {};
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw || {});
    additionalData = parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    additionalData = {};
  }
  return additionalData.overrided_product_type || product.quantity_type || '';
};

const isPillForm = (value: string) => value === 'pill' || value === 'pills';

// HL-12695: the FR promoted pill catalog (catalogs.settings.fr_promoted_pill, the Wegovy pill).
export const isPromotedPill = (product?: Product | null): boolean =>
  (product?.product_settings as { fr_promoted_pill?: boolean } | undefined)?.fr_promoted_pill === true;

// Side menu sends either "pill" or "pills". Both mean the same dosage form.
export const matchesPreferredType = (product: Product | null | undefined, preferredType: string): boolean => {
  const form = resolveDosageForm(product);
  if (form === preferredType) return true;
  return isPillForm(form) && isPillForm(preferredType);
};

/**
 * Appends the localized dosage form to a catalog title, unless the title already says it.
 * Returns the title untouched when the form has no copy (gram, unit, ...) so a raw DB value
 * is never rendered.
 */
export const titleWithDosageForm = (
  title: string,
  product: Product | null | undefined,
  languageID?: string,
): string => {
  const form = resolveDosageForm(product);
  if (!form) return title;

  const label: string | null = translate(languageID, 'choose_treatment', `dosage_form/${form}`);
  if (!label) return title;

  // "Wegovy Pill" / "Foundayo® Pill" already carry the form in the catalog name — appending it
  // again would read "Wegovy Pill Pill". Match on a word boundary so "Pill" is not found inside
  // an unrelated word. For the promoted pill (FR) the plural counts too ("Wegovy® en comprimés" already
  // says "Comprimé").
  const plural = isPromotedPill(product) ? 's?' : '';
  const alreadyMentioned = new RegExp(`(^|[^\\p{L}])${label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}${plural}([^\\p{L}]|$)`, 'iu')
    .test(title || '');
  if (alreadyMentioned) return title;

  return `${title} ${label}`;
};
